import React, { Component, type ComponentType } from 'react';
import noop from 'lodash/noop';
import type { Locale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import { fg } from '@atlassian/jira-feature-gating';

// eslint-disable-next-line jira/wrm/no-load-bridge
import {
	loadBridgeStrict,
	waitForHeritage,
	loadBridgeWeak,
} from '@atlassian/jira-common-bridge/src';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';

import type { ModalProps } from './types.tsx';
import ArchiveProject, {
	type ArchiveProjectModalOptions,
} from './ui/archive-project-modal/index.tsx';
import ConfigureMailServer from './ui/configure-mail-server-modal/index.tsx';
import ConnectIssueModal, {
	type ConnectIssueModalOptions,
} from './ui/connect-issue-modal/index.tsx';
import CreateDashboardGadgetModal, {
	type CreateDashboardGadgetModalOptions,
} from './ui/create-dashboard-gadget-modal/index.tsx';
import CustomFieldContextDelete, {
	type CustomFieldContextDeleteModalOptions,
} from './ui/custom-field-context-delete-modal/index.tsx';
import EditFilter, { type EditFilterModalOptions } from './ui/edit-filter-modal/index.tsx';
import EditPriorityMapping, {
	type EditPriorityMappingModalOptions,
} from './ui/edit-priority-mapping-modal/index.tsx';
import HardDeleteProject, {
	type HardDeleteProjectModalOptions,
} from './ui/hard-delete-project-modal/index.tsx';
import SoftDeleteProject, {
	type SoftDeleteProjectModalOptions,
} from './ui/soft-delete-project-modal/index.tsx';

type MaybeModalApi =
	| {
			close: () => void;
			mountingPromise?: Promise<undefined>;
	  }
	| undefined;
type ModalComponentType = ComponentType<ModalProps>;
type Api = {
	showCreateDashboardModal: () => void;
	showCopyDashboardModal: () => void;
	showEditDashboardModal: () => void;
	showHardDeleteProjectModal: (options: HardDeleteProjectModalOptions) => MaybeModalApi;
	showConfigureMailServerModal: () => MaybeModalApi;
	showEditFilterModal: (options: EditFilterModalOptions) => MaybeModalApi;
	showEditPriorityMappingModal: (options: EditPriorityMappingModalOptions) => MaybeModalApi;
	showCreateDashboardGadgetModal: (options: CreateDashboardGadgetModalOptions) => MaybeModalApi;
	showCustomFieldContextDeleteModal?: (
		options: CustomFieldContextDeleteModalOptions,
	) => MaybeModalApi;
	showArchiveProjectModal?: (options: ArchiveProjectModalOptions) => MaybeModalApi;
	showSoftDeleteProjectModal?: (options: SoftDeleteProjectModalOptions) => MaybeModalApi;
	showConnectIssueModal?: (options: ConnectIssueModalOptions) => MaybeModalApi;
};

type Options =
	| HardDeleteProjectModalOptions
	| EditFilterModalOptions
	| EditPriorityMappingModalOptions
	| CustomFieldContextDeleteModalOptions
	| ArchiveProjectModalOptions
	| SoftDeleteProjectModalOptions
	| ConnectIssueModalOptions
	| CreateDashboardGadgetModalOptions;

type Props = {
	locale: Locale;
};

type State = {
	ModalComponent: ModalComponentType | undefined;
};

export const LOCATION = 'page-container.navigation.legacy-async-modals';

// eslint-disable-next-line jira/react/no-class-components
export class LegacyAsyncModals extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		const createHandler =
			<CProps,>(ModalComponent: ComponentType<CProps>, method: string) =>
			(options?: Options) =>
				this.showModal(
					// @ts-expect-error - TS2322 - Type '{ onClose: () => void; onInitiateFailed?: (() => void) | undefined; onInitiateSuccess?: (() => void) | undefined; children?: ReactNode; } | { onClose: () => void; ... 6 more ...; isProjectSimplified: boolean; } | ... 5 more ... | { ...; }' is not assignable to type 'IntrinsicAttributes & CProps & { children?: ReactNode; }'.
					(modalProps) => <ModalComponent {...options} {...modalProps} />,
					undefined,
					method,
				);

		this.api = {
			showCreateDashboardModal: noop,
			showCopyDashboardModal: noop,
			showEditDashboardModal: noop,
			showEditFilterModal: createHandler(EditFilter, 'showEditFilterModal'),
			showEditPriorityMappingModal: createHandler(
				EditPriorityMapping,
				'showEditPriorityMappingModal',
			),
			showHardDeleteProjectModal: createHandler(HardDeleteProject, 'showHardDeleteProjectModal'),
			showConfigureMailServerModal: createHandler(
				ConfigureMailServer,
				'showConfigureMailServerModal',
			),
			showSoftDeleteProjectModal: createHandler(SoftDeleteProject, 'showSoftDeleteProjectModal'),
			showArchiveProjectModal: createHandler(ArchiveProject, 'showArchiveProjectModal'),
			showConnectIssueModal: createHandler(ConnectIssueModal, 'showConnectIssueModal'),
			showCustomFieldContextDeleteModal: createHandler(
				CustomFieldContextDelete,
				'showCustomFieldContextDeleteModal',
			),
			showCreateDashboardGadgetModal: createHandler(
				CreateDashboardGadgetModal,
				'showCreateDashboardGadgetModal',
			),
		};
	}

	state = { ModalComponent: undefined };

	componentDidMount() {
		if (fg('firefox_issue_view_memory_leak_v1')) {
			// eslint-disable-next-line jira/wrm/no-load-bridge
			waitForHeritage()?.then(() =>
				// eslint-disable-next-line jira/wrm/no-load-bridge
				loadBridgeStrict({
					name: 'jira/async-modals/bridge',
					wrmKeys: ['wrc!com.atlassian.jira.jira-atlaskit-plugin:async-modals'],
				})
					// @ts-expect-error - TS2571 - Object is of type 'unknown'.
					?.then((bridge) => bridge(this.api))
					.catch((e) => {
						log.safeErrorWithoutCustomerData(LOCATION, 'Cannot load the bridge', e);
					}),
			);
		} else {
			// eslint-disable-next-line jira/wrm/no-load-bridge
			loadBridgeWeak({
				name: 'jira/async-modals/bridge',
			})
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				.then((bridge) => bridge(this.api))
				.catch((e) => {
					log.safeErrorWithoutCustomerData(LOCATION, 'Cannot load the bridge', e);
				});
		}
	}

	api: Api;

	showModal(
		ModalComponent: ModalComponentType,
		mountingPromise?: Promise<undefined>,
		method?: string,
	) {
		log.safeInfoWithoutCustomerData(LOCATION, 'showModal', { method });
		if (this.state.ModalComponent) {
			log.safeErrorWithoutCustomerData(
				LOCATION,
				'Tried to show the modal while another one is open',
			);
			return undefined;
		}

		if (ModalComponent) {
			this.setState({ ModalComponent });
			return {
				close: () => this.closeModal(ModalComponent),
				mountingPromise,
			};
		}

		log.safeErrorWithoutCustomerData(LOCATION, 'Tried to show the modal which does not exist');
		return undefined;
	}

	closeModal(ModalComponent: ModalComponentType) {
		if (this.state.ModalComponent === ModalComponent) {
			this.setState({
				ModalComponent: undefined,
			});
		} else {
			log.safeErrorWithoutCustomerData(LOCATION, 'Tried to close the modal which is not open');
		}
	}

	render() {
		const { ModalComponent } = this.state;

		if (!ModalComponent) {
			return null;
		}

		const onClose = () => this.closeModal(ModalComponent);

		// @ts-expect-error - TS2604 - JSX element type 'ModalComponent' does not have any construct or call signatures.
		return <ModalComponent onClose={onClose} />;
	}
}

export default LegacyAsyncModals;
